import { relevesDemo } from './depots-releves';
import { verifierIdentite } from '@/shared/comptes/photo';
import { compresserPhoto } from '@/shared/hors-ligne/compression';
import type { Utilisateur } from '@/shared/auth/types';
import { geocoder } from '@/shared/geo/geocodage';
import { departementDuCodePostal } from '@/shared/referentiel/departements';
import { cleSiret, messageSiretPris } from '@/shared/referentiel/siret';
import type {
  DepotComptes,
  DepotEntreprises,
  DepotPartenaires,
  Depots,
  FicheEntreprise,
  FichePartenaire,
  Fichier,
  Position,
} from '@/shared/donnees/types';
import {
  adresseFichier,
  compteCourant,
  deposerFichier,
  journaliser,
  lireEtat,
  modifierEtat,
  nouvelId,
  retirerFichier,
} from './magasin';
import { chantiers, modele } from './depots-chantiers';
import { relances, sav } from './depots-sav';

/* Un léger délai : sans lui, la démo masquerait les états de chargement que
   Supabase, lui, fera voir. */
function bientot<T>(valeur: T): Promise<T> {
  return new Promise((resolve) =>
    setTimeout(() => {
      resolve(structuredClone(valeur));
    }, 120)
  );
}

function introuvable(quoi: string): never {
  throw new Error(`${quoi} introuvable : il a peut-être été supprimé entre-temps.`);
}

async function preparerFichier(dossier: string, fichier: File): Promise<Fichier> {
  const id = nouvelId('f');
  const chemin = `${dossier}/${id}-${fichier.name}`;
  await deposerFichier(chemin, fichier);
  return {
    id,
    nom: fichier.name,
    taille: fichier.size,
    typeMime: fichier.type || 'application/octet-stream',
    chemin,
    ajouteLe: new Date().toISOString(),
    ajoutePar: compteCourant()?.id ?? null,
  };
}

function nomDuChemin(chemin: string): string {
  const etat = lireEtat();
  return (
    [...etat.documentsEntreprise, ...etat.conformite].find((d) => d.chemin === chemin)?.nom ??
    chemin
  );
}

// ── Entreprises ──────────────────────────────────────────────────────────────

/** Comme db/037 : le nom de l'entreprise qui porte déjà ce SIRET, s'il y en a une. */
function siretPris(siret: string, sauf: string | null): string | null {
  const cle = cleSiret(siret);
  if (cle === '') return null;
  return (
    lireEtat().entreprises.find((e) => e.id !== sauf && cleSiret(e.siret) === cle)?.raisonSociale ??
    null
  );
}

const entreprises: DepotEntreprises = {
  lister() {
    const etat = lireEtat();
    return bientot(
      etat.entreprises.map((e) => {
        const sites = etat.sites.filter((s) => s.entrepriseId === e.id);
        const documents = etat.documentsEntreprise.filter((d) => d.entrepriseId === e.id);
        return {
          ...e,
          nbSites: sites.length,
          nbContacts: etat.contacts.filter((c) => c.entrepriseId === e.id).length,
          nbDocuments: documents.length,
          devisSigne: documents.some((d) => d.categorie === 'devis-signe'),
          departements: [...new Set(sites.map((s) => s.departement).filter(Boolean))],
        };
      })
    );
  },

  lire(id) {
    const etat = lireEtat();
    const entreprise = etat.entreprises.find((e) => e.id === id);
    if (!entreprise) return bientot(null);
    const fiche: FicheEntreprise = {
      ...entreprise,
      sites: etat.sites.filter((s) => s.entrepriseId === id),
      contacts: etat.contacts.filter((c) => c.entrepriseId === id),
      documents: etat.documentsEntreprise
        .filter((d) => d.entrepriseId === id)
        .sort((a, b) => b.ajouteLe.localeCompare(a.ajouteLe)),
    };
    return bientot(fiche);
  },

  creer(saisie) {
    const pris = siretPris(saisie.siret, null);
    if (pris) return Promise.reject(new Error(messageSiretPris(pris)));
    const entreprise = { ...saisie, id: nouvelId('e'), creeLe: new Date().toISOString() };
    modifierEtat((e) => {
      e.entreprises.push(entreprise);
      journaliser(e, 'creation', 'entreprises', entreprise.id, entreprise.raisonSociale);
    });
    return bientot(entreprise);
  },

  modifier(id, saisie) {
    const avant = lireEtat().entreprises.find((x) => x.id === id);
    const pris =
      avant && cleSiret(avant.siret) !== cleSiret(saisie.siret)
        ? siretPris(saisie.siret, id)
        : null;
    if (pris) return Promise.reject(new Error(messageSiretPris(pris)));
    modifierEtat((e) => {
      const i = e.entreprises.findIndex((x) => x.id === id);
      const avant = e.entreprises[i] ?? introuvable('Entreprise');
      const apres = { ...avant, ...saisie };
      e.entreprises[i] = apres;
      journaliser(e, 'modification', 'entreprises', id, apres.raisonSociale, avant, apres);
    });
    return bientot(undefined);
  },

  declarerNonRealisable(etapeId, note) {
    const etat = lireEtat();
    const etape = etat.etapes.find((x) => x.id === etapeId) ?? introuvable('Étape');
    const chantier =
      etat.chantiers.find((c) => c.id === etape.chantierId) ?? introuvable('Chantier');
    return this.marquerNonRealisable(chantier.entrepriseId, note);
  },

  marquerNonRealisable(id, note) {
    const motif = note.trim();
    if (motif.length < 10) {
      return Promise.reject(
        new Error('Justifiez en quelques mots pourquoi le chantier n’est pas réalisable.')
      );
    }
    modifierEtat((e) => {
      const ent = e.entreprises.find((x) => x.id === id) ?? introuvable('Entreprise');
      ent.nonRealisable = { le: new Date().toISOString(), motif, par: 'Démonstration' };
    });
    return bientot(undefined);
  },

  retablirRealisable(id) {
    modifierEtat((e) => {
      const ent = e.entreprises.find((x) => x.id === id) ?? introuvable('Entreprise');
      ent.nonRealisable = null;
    });
    return bientot(undefined);
  },

  supprimer(id) {
    modifierEtat((e) => {
      const avant = e.entreprises.find((x) => x.id === id) ?? introuvable('Entreprise');
      /* Tout le dossier part avec le client (comme db/024) : chantiers, étapes,
         preuves, tickets SAV et leurs preuves. */
      const chantiers = new Set(e.chantiers.filter((c) => c.entrepriseId === id).map((c) => c.id));
      const etapes = new Set(e.etapes.filter((x) => chantiers.has(x.chantierId)).map((x) => x.id));
      const tickets = new Set(
        e.tickets.filter((t) => chantiers.has(t.chantierId)).map((t) => t.id)
      );
      e.preuves
        .filter((x) => etapes.has(x.etapeId))
        .forEach((x) => {
          retirerFichier(x.chemin);
        });
      e.preuvesSav
        .filter((x) => tickets.has(x.ticketId))
        .forEach((x) => {
          retirerFichier(x.chemin);
        });
      e.preuves = e.preuves.filter((x) => !etapes.has(x.etapeId));
      e.preuvesSav = e.preuvesSav.filter((x) => !tickets.has(x.ticketId));
      e.etapes = e.etapes.filter((x) => !etapes.has(x.id));
      e.tickets = e.tickets.filter((t) => !tickets.has(t.id));
      e.chantiers = e.chantiers.filter((c) => !chantiers.has(c.id));
      e.documentsEntreprise
        .filter((d) => d.entrepriseId === id)
        .forEach((d) => {
          retirerFichier(d.chemin);
        });
      e.entreprises = e.entreprises.filter((x) => x.id !== id);
      e.sites = e.sites.filter((x) => x.entrepriseId !== id);
      e.contacts = e.contacts.filter((x) => x.entrepriseId !== id);
      e.documentsEntreprise = e.documentsEntreprise.filter((x) => x.entrepriseId !== id);
      journaliser(e, 'suppression', 'entreprises', id, avant.raisonSociale);
    });
    return bientot(undefined);
  },

  async ajouterSite(entrepriseId, saisie) {
    /* Le géocodage interroge le service de l'IGN : en accès libre aussi, la
       position est réelle. Un échec n'empêche pas d'enregistrer. */
    const position = await geocoder(saisie);
    modifierEtat((e) => {
      const site = {
        ...saisie,
        id: nouvelId('s'),
        entrepriseId,
        departement: departementDuCodePostal(saisie.codePostal)?.code ?? '',
        position,
      };
      e.sites.push(site);
      journaliser(e, 'creation', 'sites', site.id, site.nom);
    });
    return bientot(undefined);
  },

  async modifierSite(id, saisie) {
    const actuel = lireEtat().sites.find((x) => x.id === id) ?? introuvable('Site');
    const adresseChangee =
      actuel.adresse !== saisie.adresse ||
      actuel.codePostal !== saisie.codePostal ||
      actuel.commune !== saisie.commune;
    /* Une position corrigée à la main tient tant que l'adresse ne bouge pas. */
    const position =
      adresseChangee || actuel.position === null ? await geocoder(saisie) : actuel.position;
    modifierEtat((e) => {
      const i = e.sites.findIndex((x) => x.id === id);
      const avant = e.sites[i] ?? introuvable('Site');
      const apres = {
        ...avant,
        ...saisie,
        departement: departementDuCodePostal(saisie.codePostal)?.code ?? '',
        position,
      };
      e.sites[i] = apres;
      journaliser(e, 'modification', 'sites', id, apres.nom, avant, apres);
    });
    return bientot(undefined);
  },

  async positionnerSite(id, coordonnees) {
    const actuel = lireEtat().sites.find((x) => x.id === id) ?? introuvable('Site');
    let position: Position | null;
    if (coordonnees) {
      const { latitude, longitude } = coordonnees;
      if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
        throw new Error('Cette position n’existe pas sur la carte.');
      }
      position = { latitude, longitude, precision: 'manuelle' };
    } else {
      position = await geocoder(actuel);
    }
    modifierEtat((e) => {
      const site = e.sites.find((x) => x.id === id) ?? introuvable('Site');
      const avant = site.position;
      site.position = position;
      journaliser(e, 'modification', 'sites', id, site.nom, { position: avant }, { position });
    });
    return bientot(position);
  },

  supprimerSite(id) {
    modifierEtat((e) => {
      const avant = e.sites.find((x) => x.id === id) ?? introuvable('Site');
      if (e.chantiers.some((c) => c.siteId === id)) {
        throw new Error('Un chantier est installé sur ce site : il ne peut pas être supprimé.');
      }
      e.sites = e.sites.filter((x) => x.id !== id);
      journaliser(e, 'suppression', 'sites', id, avant.nom);
    });
    return bientot(undefined);
  },

  ajouterContact(entrepriseId, saisie) {
    modifierEtat((e) => {
      const contact = { ...saisie, id: nouvelId('c'), entrepriseId };
      e.contacts.push(contact);
      journaliser(e, 'creation', 'contacts', contact.id, contact.nom);
    });
    return bientot(undefined);
  },

  modifierContact(id, saisie) {
    modifierEtat((e) => {
      const i = e.contacts.findIndex((x) => x.id === id);
      const avant = e.contacts[i] ?? introuvable('Contact');
      const apres = { ...avant, ...saisie };
      e.contacts[i] = apres;
      journaliser(e, 'modification', 'contacts', id, apres.nom, avant, apres);
    });
    return bientot(undefined);
  },

  supprimerContact(id) {
    modifierEtat((e) => {
      const avant = e.contacts.find((x) => x.id === id) ?? introuvable('Contact');
      e.contacts = e.contacts.filter((x) => x.id !== id);
      journaliser(e, 'suppression', 'contacts', id, avant.nom);
    });
    return bientot(undefined);
  },

  async ajouterDocument(entrepriseId, fichier, categorie) {
    const prepare = await preparerFichier(`entreprises/${entrepriseId}`, fichier);
    modifierEtat((e) => {
      e.documentsEntreprise.push({ ...prepare, entrepriseId, categorie });
      journaliser(e, 'creation', 'documents_entreprise', prepare.id, prepare.nom);
    });
    return bientot(undefined);
  },

  supprimerDocument(id) {
    modifierEtat((e) => {
      const avant = e.documentsEntreprise.find((x) => x.id === id) ?? introuvable('Document');
      retirerFichier(avant.chemin);
      e.documentsEntreprise = e.documentsEntreprise.filter((x) => x.id !== id);
      journaliser(e, 'suppression', 'documents_entreprise', id, avant.nom);
    });
    return bientot(undefined);
  },

  adresseDocument(chemin) {
    return adresseFichier(chemin, nomDuChemin(chemin));
  },
};

// ── Partenaires ──────────────────────────────────────────────────────────────

const partenaires: DepotPartenaires = {
  lister() {
    const etat = lireEtat();
    return bientot(
      etat.partenaires.map((p) => ({
        ...p,
        conformite: etat.conformite.filter((d) => d.partenaireId === p.id),
        nbComptes: etat.comptes.filter((c) => c.partenaireId === p.id).length,
      }))
    );
  },

  lire(id) {
    const etat = lireEtat();
    const partenaire = etat.partenaires.find((p) => p.id === id);
    if (!partenaire) return bientot(null);
    const fiche: FichePartenaire = {
      ...partenaire,
      conformite: etat.conformite.filter((d) => d.partenaireId === id),
      comptes: etat.comptes.filter((c) => c.partenaireId === id),
      contacts: (etat.contactsPartenaires ?? []).filter((c) => c.partenaireId === id),
    };
    return bientot(fiche);
  },

  creer(saisie) {
    const partenaire = { ...saisie, id: nouvelId('p'), creeLe: new Date().toISOString() };
    modifierEtat((e) => {
      e.partenaires.push(partenaire);
      journaliser(e, 'creation', 'partenaires', partenaire.id, partenaire.raisonSociale);
    });
    return bientot(partenaire);
  },

  modifier(id, saisie) {
    modifierEtat((e) => {
      const i = e.partenaires.findIndex((x) => x.id === id);
      const avant = e.partenaires[i] ?? introuvable('Partenaire');
      const apres = { ...avant, ...saisie };
      e.partenaires[i] = apres;
      for (const compte of e.comptes) {
        if (compte.partenaireId === id) compte.partenaireNom = apres.raisonSociale;
      }
      journaliser(e, 'modification', 'partenaires', id, apres.raisonSociale, avant, apres);
    });
    return bientot(undefined);
  },

  supprimer(id) {
    modifierEtat((e) => {
      const avant = e.partenaires.find((x) => x.id === id) ?? introuvable('Partenaire');
      /* Comme db/024 : ses étapes et tickets restent aux chantiers, sans lui ni
         dates ; ses comptes partent avec lui. */
      for (const x of e.etapes) {
        if (x.partenaireId !== id) continue;
        x.partenaireId = null;
        if (x.statut !== 'validee' && x.statut !== 'a-valider') {
          x.debutPrevu = null;
          x.finPrevue = null;
        }
        if (x.statut === 'planifiee') x.statut = 'a-planifier';
      }
      for (const t of e.tickets) {
        if (t.partenaireId !== id) continue;
        t.partenaireId = null;
        if (t.statut !== 'resolu' && t.statut !== 'clos') {
          t.debutPrevu = null;
          t.finPrevue = null;
        }
      }
      for (const c of e.chantiers) {
        if (c.partenaireId === id) c.partenaireId = null;
      }
      e.comptes = e.comptes.filter((c) => c.partenaireId !== id);
      e.conformite
        .filter((d) => d.partenaireId === id)
        .forEach((d) => {
          retirerFichier(d.chemin);
        });
      e.partenaires = e.partenaires.filter((x) => x.id !== id);
      e.contactsPartenaires = (e.contactsPartenaires ?? []).filter((x) => x.partenaireId !== id);
      e.conformite = e.conformite.filter((x) => x.partenaireId !== id);
      journaliser(e, 'suppression', 'partenaires', id, avant.raisonSociale);
    });
    return bientot(undefined);
  },

  async ajouterConformite(partenaireId, fichier, type, expireLe) {
    const prepare = await preparerFichier(`conformite/${partenaireId}`, fichier);
    modifierEtat((e) => {
      e.conformite.push({ ...prepare, partenaireId, type, expireLe });
      journaliser(e, 'creation', 'documents_conformite', prepare.id, prepare.nom);
    });
    return bientot(undefined);
  },

  supprimerConformite(id) {
    modifierEtat((e) => {
      const avant = e.conformite.find((x) => x.id === id) ?? introuvable('Document');
      retirerFichier(avant.chemin);
      e.conformite = e.conformite.filter((x) => x.id !== id);
      journaliser(e, 'suppression', 'documents_conformite', id, avant.nom);
    });
    return bientot(undefined);
  },

  adresseDocument(chemin) {
    return adresseFichier(chemin, nomDuChemin(chemin));
  },

  ajouterContact(partenaireId, saisie) {
    modifierEtat((e) => {
      const contact = { ...saisie, id: nouvelId('cp'), partenaireId };
      e.contactsPartenaires = [...(e.contactsPartenaires ?? []), contact];
      journaliser(e, 'creation', 'contacts_partenaires', contact.id, contact.nom);
    });
    return bientot(undefined);
  },

  modifierContact(id, saisie) {
    modifierEtat((e) => {
      const liste = e.contactsPartenaires ?? [];
      const avant = liste.find((x) => x.id === id) ?? introuvable('Contact');
      const apres = { ...avant, ...saisie };
      e.contactsPartenaires = liste.map((x) => (x.id === id ? apres : x));
      journaliser(e, 'modification', 'contacts_partenaires', id, apres.nom, avant, apres);
    });
    return bientot(undefined);
  },

  supprimerContact(id) {
    modifierEtat((e) => {
      const liste = e.contactsPartenaires ?? [];
      const avant = liste.find((x) => x.id === id) ?? introuvable('Contact');
      e.contactsPartenaires = liste.filter((x) => x.id !== id);
      journaliser(e, 'suppression', 'contacts_partenaires', id, avant.nom);
    });
    return bientot(undefined);
  },

  async changerLogo(partenaireId, fichier) {
    /* En accès libre, le logo réduit tient dans le navigateur. */
    const logo = fichier ? await enDonnees(await compresserPhoto(fichier, 256)) : null;
    modifierEtat((e) => {
      const p = e.partenaires.find((x) => x.id === partenaireId) ?? introuvable('Partenaire');
      p.logo = logo;
      for (const compte of e.comptes) {
        if (compte.partenaireId === partenaireId) compte.partenaireLogo = logo;
      }
    });
  },

  inviter(partenaireId, invitation) {
    const email = invitation.email.trim().toLowerCase();
    const etat = lireEtat();
    if (etat.comptes.some((c) => c.email === email)) {
      return Promise.reject(new Error('Un compte existe déjà pour cette adresse.'));
    }
    const partenaire =
      etat.partenaires.find((p) => p.id === partenaireId) ?? introuvable('Partenaire');
    const compte: Utilisateur = {
      id: nouvelId('u'),
      email,
      nom: invitation.nom.trim(),
      role: 'partenaire',
      partenaireId,
      partenaireNom: partenaire.raisonSociale,
      actif: true,
    };
    modifierEtat((e) => {
      e.comptes.push(compte);
      journaliser(e, 'creation', 'profils', compte.id, compte.email);
    });
    return bientot(undefined);
  },
};

/** Un fichier en adresse « data: », pour le garder dans le navigateur. */
function enDonnees(fichier: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const lecteur = new FileReader();
    lecteur.onload = () => {
      resolve(typeof lecteur.result === 'string' ? lecteur.result : '');
    };
    lecteur.onerror = () => {
      reject(new Error('La photo n’a pas pu être lue.'));
    };
    lecteur.readAsDataURL(fichier);
  });
}

// ── Comptes ──────────────────────────────────────────────────────────────────

const comptes: DepotComptes = {
  lister() {
    return bientot(lireEtat().comptes);
  },

  rattacher(compteId, role, partenaireId) {
    modifierEtat((e) => {
      const compte = e.comptes.find((c) => c.id === compteId) ?? introuvable('Compte');
      const avant = { role: compte.role, partenaire_id: compte.partenaireId };
      compte.role = role;
      compte.partenaireId = role === 'bureau' ? null : partenaireId;
      compte.partenaireNom =
        e.partenaires.find((p) => p.id === compte.partenaireId)?.raisonSociale ?? null;
      journaliser(e, 'modification', 'profils', compteId, compte.email, avant, {
        role: compte.role,
        partenaire_id: compte.partenaireId,
      });
    });
    return bientot(undefined);
  },

  modifierIdentite(compteId, identite) {
    const propre = verifierIdentite(identite);
    modifierEtat((e) => {
      const compte = e.comptes.find((c) => c.id === compteId) ?? introuvable('Compte');
      const avant = compte.nom;
      compte.prenom = propre.prenom;
      compte.nomFamille = propre.nomFamille;
      compte.nom = `${propre.prenom} ${propre.nomFamille}`.trim();
      journaliser(
        e,
        'modification',
        'profils',
        compteId,
        compte.nom,
        { nom: avant },
        { nom: compte.nom }
      );
    });
    return bientot(undefined);
  },

  inviterBureau(invitation) {
    const email = invitation.email.trim().toLowerCase();
    const propre = verifierIdentite(invitation);
    if (lireEtat().comptes.some((c) => c.email === email)) {
      return Promise.reject(new Error('Un compte existe déjà pour cette adresse.'));
    }
    const compte: Utilisateur = {
      id: nouvelId('u'),
      email,
      nom: `${propre.prenom} ${propre.nomFamille}`.trim(),
      prenom: propre.prenom,
      nomFamille: propre.nomFamille,
      role: 'bureau',
      niveau: invitation.niveau,
      partenaireId: null,
      partenaireNom: null,
      actif: true,
    };
    modifierEtat((e) => {
      e.comptes.push(compte);
      journaliser(e, 'creation', 'profils', compte.id, compte.email);
    });
    return bientot(undefined);
  },

  changerNiveau(compteId, niveau) {
    modifierEtat((e) => {
      const compte = e.comptes.find((c) => c.id === compteId) ?? introuvable('Compte');
      if (compte.niveau === 'ceo' && compte.id !== compteCourant()?.id) {
        throw new Error('Le compte du CEO ne se modifie que par lui-même.');
      }
      if (niveau === 'ceo') throw new Error('Seul le CEO peut donner ce niveau.');
      const avant = compte.niveau ?? null;
      compte.niveau = niveau;
      journaliser(
        e,
        'modification',
        'profils',
        compteId,
        compte.email,
        { niveau: avant },
        { niveau }
      );
    });
    return bientot(undefined);
  },

  async changerPhoto(compteId, fichier) {
    /* En accès libre, la photo réduite tient dans le navigateur, en clair. */
    const photo = fichier ? await enDonnees(await compresserPhoto(fichier, 256)) : null;
    modifierEtat((e) => {
      const compte = e.comptes.find((c) => c.id === compteId) ?? introuvable('Compte');
      compte.photo = photo;
    });
  },

  definirActif(compteId, actif) {
    modifierEtat((e) => {
      const compte = e.comptes.find((c) => c.id === compteId) ?? introuvable('Compte');
      if (!actif && compte.id === compteCourant()?.id) {
        throw new Error('Vous ne pouvez pas suspendre le compte avec lequel vous êtes connecté.');
      }
      const avant = compte.actif;
      compte.actif = actif;
      journaliser(
        e,
        'modification',
        'profils',
        compteId,
        compte.email,
        { actif: avant },
        { actif }
      );
    });
    return bientot(undefined);
  },
};

export const depotsDemo: Depots = {
  entreprises,
  partenaires,
  comptes,
  chantiers,
  modele,
  sav,
  relances,
  releves: relevesDemo,
};
